<script setup>
import BreezeButton from '@/Components/Button.vue';
import BreezeCheckbox from '@/Components/Checkbox.vue';
import BreezeGuestLayout from '@/Layouts/Guest.vue';
import BreezeInput from '@/Components/Input.vue';
import BreezeLabel from '@/Components/Label.vue';
import BreezeValidationErrors from '@/Components/ValidationErrors.vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import {onMounted, ref} from "vue";

const props = defineProps({
    canResetPassword: Boolean,
    status: String,
	google_client_id: String
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
	magicButton: false,
	jwt: ''
});

const lastUsed = ref(null);

onMounted(() => {
	const storedValue = localStorage.getItem('lastUsed');
	if (storedValue) {
		lastUsed.value = storedValue;
	}
});

const submit = () => {
	console.log("login submit");
    form.post('login', {
        onFinish: () => {
			console.log("finished");
			form.reset('password');
			form.magicButton = false;
		},
    });
};

const loginWithGoogleButton = ref(null)

onMounted(() => {
	const script = document.createElement("script");
	script.src = "https://accounts.google.com/gsi/client";
	script.async = true;
	document.head.appendChild(script);
	// Load the Google Identity Services script

	script.onload = () => {
		window.google.accounts.id.initialize({
			client_id: props.google_client_id,
			callback: handleCredentialResponse
		})

		window.google.accounts.id.renderButton(
			loginWithGoogleButton.value,
			{ theme: "outline", size: "large", width: '400' }
		)

		// window.google.accounts.id.prompt() // this is "one tap" login in the top right corner
	}
});


function handleCredentialResponse(response) {
	form.jwt = response.credential;
	submit();
}

const selectedMagicLinkLogin = ref(false);
const selectedPasswordLogin = ref(false);

function clearSelectedOptions() {
	form.clearErrors();
	selectedPasswordLogin.value = false;
	selectedMagicLinkLogin.value = false;
}

</script>

<template>
    <BreezeGuestLayout>
        <Head title="Log in" />


        <BreezeValidationErrors class="mb-4" v-if="selectedPasswordLogin || selectedPasswordLogin"/>

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600">
            {{ status }}
        </div>




		<div class="relative">
			<div v-show="!selectedMagicLinkLogin && !selectedPasswordLogin" ref="loginWithGoogleButton" class="pt-2"></div>
			<span v-show="lastUsed === 'google' && !selectedMagicLinkLogin && !selectedPasswordLogin" class="-translate-y-1/2 absolute -left-[6.5rem] mt-0.5 top-1/2 transform flex items-center">
				<div class="text-xs text-white bg-black/80 px-3 py-px rounded-lg flex">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="mr-1 size-4">
						<path fill-rule="evenodd" d="M14 6a4 4 0 0 1-4.899 3.899l-1.955 1.955a.5.5 0 0 1-.353.146H5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2.293a.5.5 0 0 1 .146-.353l3.955-3.955A4 4 0 1 1 14 6Zm-4-2a.75.75 0 0 0 0 1.5.5.5 0 0 1 .5.5.75.75 0 0 0 1.5 0 2 2 0 0 0-2-2Z" clip-rule="evenodd" />
					</svg>
					Last used
				</div>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 text-gray-700">
					<path fill-rule="evenodd" d="M15.28 9.47a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L13.69 10 9.97 6.28a.75.75 0 0 1 1.06-1.06l4.25 4.25ZM6.03 5.22l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L8.69 10 4.97 6.28a.75.75 0 0 1 1.06-1.06Z" clip-rule="evenodd" />
				</svg>
			</span>
		</div>






		<div class="space-y-3" v-show="!selectedMagicLinkLogin && !selectedPasswordLogin">
			<div class="flex items-center mt-4">
				<hr class="flex-1 my-5">
				<span class="mx-4 text-sm text-gray-800">Or continue with</span>
				<hr class="flex-1 my-5">
			</div>

			<div class="relative">
				<button @click="selectedMagicLinkLogin = true" class="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5 mr-2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
					</svg>
					Sign in with magic link
				</button>
				<span v-show="lastUsed === 'magic' && (!selectedMagicLinkLogin && !selectedPasswordLogin)" class="-translate-y-1/2 absolute -left-[6.5rem] top-1/2 transform flex items-center">
					<div class="text-xs text-white bg-black/80 px-3 py-px rounded-lg flex">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="mr-1 size-4">
							<path fill-rule="evenodd" d="M14 6a4 4 0 0 1-4.899 3.899l-1.955 1.955a.5.5 0 0 1-.353.146H5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2.293a.5.5 0 0 1 .146-.353l3.955-3.955A4 4 0 1 1 14 6Zm-4-2a.75.75 0 0 0 0 1.5.5.5 0 0 1 .5.5.75.75 0 0 0 1.5 0 2 2 0 0 0-2-2Z" clip-rule="evenodd" />
						</svg>
						Last used
					</div>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 text-gray-700">
						<path fill-rule="evenodd" d="M15.28 9.47a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L13.69 10 9.97 6.28a.75.75 0 0 1 1.06-1.06l4.25 4.25ZM6.03 5.22l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L8.69 10 4.97 6.28a.75.75 0 0 1 1.06-1.06Z" clip-rule="evenodd" />
					</svg>
				</span>
			</div>

			<div class="relative">
				<button @click="selectedPasswordLogin = true" class="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5 mr-2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
					</svg>
					Sign in with password
				</button>
				<span v-if="lastUsed === 'password'" class="-translate-y-1/2 absolute -left-[6.5rem] top-1/2 transform flex items-center">
					<div class="text-xs text-white bg-black/80 px-3 py-px rounded-lg flex">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="mr-1 size-4">
							<path fill-rule="evenodd" d="M14 6a4 4 0 0 1-4.899 3.899l-1.955 1.955a.5.5 0 0 1-.353.146H5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2.293a.5.5 0 0 1 .146-.353l3.955-3.955A4 4 0 1 1 14 6Zm-4-2a.75.75 0 0 0 0 1.5.5.5 0 0 1 .5.5.75.75 0 0 0 1.5 0 2 2 0 0 0-2-2Z" clip-rule="evenodd" />
						</svg>
						Last used
					</div>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 text-gray-700">
						<path fill-rule="evenodd" d="M15.28 9.47a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L13.69 10 9.97 6.28a.75.75 0 0 1 1.06-1.06l4.25 4.25ZM6.03 5.22l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L8.69 10 4.97 6.28a.75.75 0 0 1 1.06-1.06Z" clip-rule="evenodd" />
					</svg>
				</span>
			</div>

		</div>





        <form @submit.prevent="submit" v-show="selectedMagicLinkLogin || selectedPasswordLogin">
            <div>
                <BreezeLabel for="email" value="Email" />
                <BreezeInput id="email" type="email" class="mt-1 block w-full" v-model="form.email" required autofocus autocomplete="username" />
            </div>


			<div class="flex items-center justify-end mt-4" v-if="selectedMagicLinkLogin">
				<BreezeButton @click="form.magicButton = true" class="ml-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
					Send Magic Link
				</BreezeButton>
			</div>

            <div class="mt-4" v-show="selectedPasswordLogin">
                <BreezeLabel for="password" value="Password" />
                <BreezeInput id="password" type="password" class="mt-1 block w-full" v-model="form.password" :required="!form.magicButton" autocomplete="current-password" />
            </div>

            <div class="block mt-4" v-show="selectedPasswordLogin">
                <label class="flex items-center">
                    <BreezeCheckbox name="remember" v-model:checked="form.remember" />
                    <span class="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
            </div>

            <div class="flex items-center justify-end mt-4" v-show="selectedPasswordLogin">
                <Link v-if="canResetPassword" :href="route('password.request')" class="underline text-sm text-gray-600 hover:text-gray-900">
                    Forgot your password?
                </Link>

                <BreezeButton class="ml-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing" v-if="selectedPasswordLogin">
                    Password Log In
                </BreezeButton>
            </div>

			<button
				type="button"
				class="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:underline mt-6"
				@click="clearSelectedOptions">
				<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5 mr-2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
				</svg>

				Back to all sign in options
			</button>


        </form>



		<hr class="my-5">
		<Link :href="route('register')" class="underline text-sm text-gray-600 hover:text-gray-900 flex justify-end">
			Don't have an account?
		</Link>
    </BreezeGuestLayout>

</template>
